import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import ModalBasicLayout from "../ModalBasicLayout";
import { ConfirmAlert, SimpleAlert } from "../../Alerts/Alerts";
import {
  ChatSidebar,
  ImageEditTopBar,
  ResponsiveSidebarContainer,
  WorkModeSelector,
} from "../ModalImageEdit/shared";
import { ImageCanvas, SingleChatMessages } from "../ModalImageEdit/single";
import LayerCanvas from "../ModalImageEdit/layers/LayerCanvas";
import LayersSidebar from "../ModalImageEdit/layers/LayersSidebar";
import InstanceCanvas from "./InstanceCanvas";
import InstanceLayersSidebar from "./InstanceLayersSidebar";
import InstancePropertiesPanel from "./InstancePropertiesPanel";
import useTemplateInstance from "../../../features/creativeTemplates/instance/useTemplateInstance";
import { produceItemNeedsLayerReanalysis } from "../../../features/creativeTemplates/produce/produceRows";
import { useResizableSidebarWidth } from "../../../features/creativeTemplates/editor/useResizableSidebarWidth";
import useTextHighlightSelection, {
  applyIfHighlightField,
} from "../../../features/creativeTemplates/editor/useTextHighlightSelection";
import { alignSelectedBoxes } from "../../../features/creativeTemplates/editor/alignSelectedLayers";
import {
  useImageEdit,
  useChatMessages,
  useImageLayers,
  useAnalyzeImageLayers,
  PROMPTS_CHECKPOINT_SIZE,
} from "../ModalImageEdit/hooks";
import { buildEditImagePromptsForJob } from "../ModalImageEdit/hooks/imageEditCheckpoint";
import {
  IMAGE_EDIT_WORK_MODE,
  LAYER_TYPE,
  getLayerType,
  layerToInspectorItem,
  pixelsToNormalized,
} from "../ModalImageEdit/layers/layerGeometry";
import { confirmAnalyzeDetectsText } from "../../../utils/confirmAnalyzeDetectsText";
import { getImageDimensionsFromUrl } from "../../../features/creativeTemplates/utils/getImageDimensionsFromFile";
import { isLayerizeImageTooSmall } from "../../../features/creativeTemplates/utils/layerizeImageConstraints";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { getKeyDatabase } from "../../../services/public";
import { startSaveEditedProduceCreative } from "../../../actions/creativeTemplatesProduce";
import { startSaveEditedLayeredImage } from "../../../actions/imageLayers";
import { startGetBrandInformation } from "../../../actions/adsCampaigns";
import { getIsBlockedActions } from "../../../actions/getters";
import {
  startListenerMergedFonts,
  stopListenerMergedFonts,
} from "../../../actions/fonts";
import { loadFontFaces } from "../../../features/creativeTemplates/utils/fonts";
import {
  blurActiveEditable,
  isCopyHotkey,
  isDeleteHotkey,
  isNativeEditableTarget,
  isPasteHotkey,
} from "../../../features/creativeTemplates/utils/canvasClipboard";
import {
  listStoredInlineReplacements,
  urlsFromInlineReplacementAssetRefs,
} from "../../../features/creativeTemplates/utils/textInlineReplacements";
import { UploadFilesContext } from "../../../contexts/UploadFilesContext";
import { useCreditActionCosts } from "../../../hooks/useCreditActionCosts";
import { validateModalImageEditTextFields } from "../../../pages/Settings/BrandSetup/utils/blockedBrandsValidation";
import {
  BUTTON_CLOSE,
  BUTTON_CONTINUE,
  BUTTON_GO_BACK,
  MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_DESCRIPTION,
  MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_TITLE,
  MODAL_IMAGE_EDIT_CLOSE_DESCRIPTION,
  MODAL_IMAGE_EDIT_CLOSE_TITLE,
  TEMPLATES_ANALYZE_IMAGE_TOO_SMALL,
  TEMPLATES_KEEP_EDITING,
  TEMPLATES_NEW_LAYER,
  TEMPLATES_PRODUCE_ADD_IMAGE,
  TEMPLATES_PRODUCE_ADD_TEXT,
  TEMPLATES_PRODUCE_EDIT_TITLE,
  TEMPLATES_PRODUCE_SAVE_ERROR,
  WARNING,
} from "../../../i18n/keysTranslations";

import { LaunchError } from "../../../utils/errors";

const propertiesPanelSx = {
  width: { xs: "100%", md: 320 },
  maxWidth: { xs: "100%", md: 320 },
  height: { xs: "auto", md: "100%" },
  maxHeight: { xs: "40%", md: "none" },
  flexShrink: 0,
  bgcolor: "background.paper",
  borderLeft: { xs: 0, md: "1px solid rgba(0, 0, 0, 0.12)" },
  borderTop: { xs: "1px solid rgba(0, 0, 0, 0.12)", md: 0 },
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  minHeight: 0,
  overflow: "hidden",
};

const ModalCreativeTemplateEdit = ({ isOpen, onClose, produceID, item }) => {
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();
  const theme = useTheme();
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { uploadFile } = useContext(UploadFilesContext) || {};
  const { userID } = dispatch(getPathDatabaseByRole());
  const isPlanActive = useSelectorApp((state) => state?.payments?.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state?.payments?.subscriptionPlanInfo?.isTrial
  );
  const originalImageUrl = item?.url || "";
  const itemID = item?.id || "";
  const instance = item?.instance || null;

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const {
    width: layersSidebarWidth,
    onResizePointerDown: onResizeLayersSidebar,
  } = useResizableSidebarWidth({
    defaultWidth: 420,
    minWidth: 280,
    maxWidth: 640,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);
  const [imageUrl, setImageUrl] = useState(originalImageUrl);
  const [blockBaseImageUrl, setBlockBaseImageUrl] = useState(originalImageUrl);
  const [checkpointPromptsHistory, setCheckpointPromptsHistory] = useState([]);
  const [workMode, setWorkMode] = useState(IMAGE_EDIT_WORK_MODE.GENERATIVE);
  const [imageLayerEditingTextID, setImageLayerEditingTextID] = useState(null);
  const [businessLookAndFeel, setBusinessLookAndFeel] = useState("");
  const [syncedItemID, setSyncedItemID] = useState(itemID);
  const [wasOpen, setWasOpen] = useState(isOpen);
  const fontListenerRefs = useRef({ commonRef: null, advertiserRef: null });
  const fontFamiliesRef = useRef([]);
  const brandFontFamilyRef = useRef("");
  const acknowledgedLayerLossForUrlRef = useRef(null);
  const isEnteringLayersModeRef = useRef(false);
  const isCloseConfirmOpenRef = useRef(false);

  if (itemID !== syncedItemID) {
    setSyncedItemID(itemID);
    setImageUrl(originalImageUrl);
    setBlockBaseImageUrl(originalImageUrl);
    setCheckpointPromptsHistory([]);
    setWorkMode(IMAGE_EDIT_WORK_MODE.GENERATIVE);
    setImageLayerEditingTextID(null);
  }
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setImageUrl(originalImageUrl);
      setBlockBaseImageUrl(originalImageUrl);
      setCheckpointPromptsHistory([]);
      setWorkMode(IMAGE_EDIT_WORK_MODE.GENERATIVE);
      setImageLayerEditingTextID(null);
      acknowledgedLayerLossForUrlRef.current = null;
    }
  }

  const resolvedImageUrl = imageUrl || originalImageUrl;
  const needsReanalysis = produceItemNeedsLayerReanalysis({
    item,
    currentImageUrl: resolvedImageUrl,
  });
  const isLayersMode = workMode === IMAGE_EDIT_WORK_MODE.LAYERS;

  const useInstanceLayers =
    isLayersMode && !needsReanalysis && Boolean(instance);

  const {
    width,
    height,
    nodes,
    nodeLayouts,
    items,
    selectedID,
    selectedIDs,
    selectedItem,
    editingTextID,
    expandedIDs,
    selectLayer,
    selectLayers,
    setEditingTextID,
    setExpandedIDs,
    updateBox,
    updateBoxes,
    patchSelected,
    renameLayer,
    setTextValue,
    addLayer,
    addImageLayers,
    deleteLayer,
    copyLayer,
    pasteLayer,
    reorderSiblings,
    toggleLock,
    toggleVisible,
    replaceImage,
    assignInlineReplacementAsset,
    undo,
    redo,
    endCoalesce,
    getPersistInstance,
  } = useTemplateInstance({
    instance,
    enabled: isOpen && Boolean(instance) && !needsReanalysis,
    createID: () => dispatch(getKeyDatabase()),
    fontFamiliesRef,
    brandFontFamilyRef,
  });

  const {
    textSelection: instanceTextSelection,
    keepTextEditingRef: keepInstanceTextEditingRef,
    onTextSelectionChange: onInstanceTextSelectionChange,
    styleForHighlightField: styleForInstanceHighlightField,
    isTextRangeSelected: isInstanceTextRangeSelected,
    selectedPhrase: instanceSelectedPhrase,
  } = useTextHighlightSelection({
    selectedID,
    editingTextID,
    canvasSelector: "[data-instance-canvas]",
    nodesVersion: items,
    getNode: (nodeID) => items.find((item) => item.id === nodeID)?.node || null,
    // The instance paints the resolved value, not the template's origin text.
    getNodeText: (node, nodeID) =>
      String(items.find((item) => item.id === nodeID)?.text || ""),
    persistHighlights: ({ nodeID, highlights }) => {
      const node = items.find((item) => item.id === nodeID)?.node;
      if (!node) return;
      const style = { ...(node.style || {}) };
      if (Array.isArray(highlights) && highlights.length) {
        style.highlights = highlights;
      } else {
        delete style.highlights;
      }
      patchSelected({ nodeID, patch: { style } });
    },
  });

  const { chatMessages, addMessage, replaceThinkingMessage, chatScrollRef } =
    useChatMessages();
  const { isProcessing, isEditing, editImage, createThinkingMessage } =
    useImageEdit();
  const {
    layers,
    canvasWidth,
    canvasHeight,
    layersDirty,
    selectedLayerId,
    selectedLayerIds,
    selectLayer: selectImageLayer,
    selectLayers: selectImageLayers,
    updateLayerBox,
    updateLayerBoxes,
    addLayersFromUrls,
    addTextLayer,
    updateLayerText,
    updateLayerStyle,
    replaceLayerImage,
    deleteLayer: deleteImageLayer,
    duplicateLayer,
    reorderLayers,
    renameLayer: renameImageLayer,
    toggleLayerLock,
    toggleLayerVisible,
    persistSnapshotNow,
    hasSnapshot,
    isLoadingSnapshot,
  } = useImageLayers({
    sourceUrl: resolvedImageUrl,
    enabled: isOpen && (needsReanalysis || !instance),
    hotkeysEnabled: isOpen && isLayersMode && (needsReanalysis || !instance),
    fontFamiliesRef,
    brandFontFamilyRef,
  });
  const { analyze, isAnalyzing, errorMessage } = useAnalyzeImageLayers({
    sourceUrl: resolvedImageUrl,
  });

  const selectedImageLayer = useMemo(
    () => layers.find((layer) => layer.id === selectedLayerId) || null,
    [layers, selectedLayerId]
  );
  const selectedImageInspectorItem = useMemo(
    () =>
      layerToInspectorItem({
        layer: selectedImageLayer,
        canvasWidth,
        canvasHeight,
      }),
    [canvasHeight, canvasWidth, selectedImageLayer]
  );

  const {
    textSelection: imageTextSelection,
    keepTextEditingRef: keepImageTextEditingRef,
    onTextSelectionChange: onImageTextSelectionChange,
    styleForHighlightField: styleForImageHighlightField,
    isTextRangeSelected: isImageTextRangeSelected,
    selectedPhrase: imageSelectedPhrase,
  } = useTextHighlightSelection({
    selectedID: selectedLayerId,
    editingTextID: imageLayerEditingTextID,
    canvasSelector: "[data-image-layers-canvas]",
    nodesVersion: layers,
    getNode: (layerId) => layers.find((layer) => layer.id === layerId) || null,
    getNodeText: (layer) => String(layer?.text || ""),
    // Image layers carry no source kind, so being a text layer is enough.
    canHighlight: (layer) =>
      Boolean(layer) && getLayerType(layer) === LAYER_TYPE.TEXT,
    persistHighlights: ({ nodeID, highlights }) => {
      updateLayerStyle(nodeID, {
        highlights:
          Array.isArray(highlights) && highlights.length ? highlights : null,
      });
    },
  });

  const onAlignInstanceLayers = (align) => {
    const ids = selectedIDs.filter(Boolean);
    if (ids.length < 2 || !width || !height) return;
    const selectedBoxes = items
      .filter(
        (item) =>
          ids.includes(item.id) && !item.isBackground && !item.node?.locked
      )
      .map((item) => ({
        id: item.id,
        x: item.box.x,
        y: item.box.y,
        width: item.box.width,
        height: item.box.height,
      }));
    if (selectedBoxes.length < 2) return;
    updateBoxes(
      alignSelectedBoxes({ boxes: selectedBoxes, align }).map((box) => ({
        id: box.id,
        nx: box.x / width,
        ny: box.y / height,
        nw: box.width / width,
        nh: box.height / height,
      }))
    );
  };

  const onAlignImageLayers = (align) => {
    const ids = selectedLayerIds.filter(Boolean);
    if (ids.length < 2) return;
    const selectedBoxes = layers
      .filter(
        (layer) =>
          ids.includes(layer.id) && !layer.isBackground && !layer.locked
      )
      .map((layer) => ({
        id: layer.id,
        x: layer.x,
        y: layer.y,
        width: layer.width,
        height: layer.height,
      }));
    if (selectedBoxes.length < 2) return;
    updateLayerBoxes(
      alignSelectedBoxes({ boxes: selectedBoxes, align }).map((box) => ({
        id: box.id,
        x: box.x,
        y: box.y,
        width: box.width,
        height: box.height,
      }))
    );
  };

  const currentImageHasLayerAnalysis = needsReanalysis
    ? hasSnapshot || layers.length > 0
    : Boolean(instance);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const confirmAiEditLosesLayers = async () => {
    if (!currentImageHasLayerAnalysis) return true;
    if (acknowledgedLayerLossForUrlRef.current === resolvedImageUrl) {
      return true;
    }

    const result = await ConfirmAlert({
      title: t(MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_TITLE),
      text: t(MODAL_IMAGE_EDIT_AI_LOSES_LAYERS_DESCRIPTION),
      confirmButtonText: t(BUTTON_CONTINUE),
      cancelButtonText: t(BUTTON_GO_BACK),
      icon: "warning",
    });

    if (!result?.isConfirmed) return false;
    acknowledgedLayerLossForUrlRef.current = resolvedImageUrl;
    return true;
  };

  const onAnalyzeLayers = useCallback(
    async ({ onUsageConfirmed } = {}) => {
      if (!resolvedImageUrl) return { ok: false };
      try {
        const dimensions = await getImageDimensionsFromUrl({
          url: resolvedImageUrl,
        });
        if (isLayerizeImageTooSmall(dimensions)) {
          SimpleAlert({
            title: t(WARNING),
            text: t(TEMPLATES_ANALYZE_IMAGE_TOO_SMALL),
            icon: "error",
          });
          return { ok: false };
        }
      } catch {
        // Continue analysis if image dimensions cannot be read.
      }
      const confirmation = await confirmAnalyzeDetectsText({ t });
      if (!confirmation.proceed) return { ok: false };
      return analyze({
        extractText: confirmation.extractText,
        onUsageConfirmed,
      });
    },
    [analyze, resolvedImageUrl, t]
  );

  const onWorkModeChange = async (nextMode) => {
    if (nextMode === workMode || isEnteringLayersModeRef.current) return;
    if (isAnalyzing && nextMode === IMAGE_EDIT_WORK_MODE.GENERATIVE) {
      return;
    }
    if (
      nextMode === IMAGE_EDIT_WORK_MODE.GENERATIVE &&
      workMode === IMAGE_EDIT_WORK_MODE.LAYERS
    ) {
      const canContinue = await confirmAiEditLosesLayers();
      if (!canContinue) return;
    }
    if (nextMode === IMAGE_EDIT_WORK_MODE.LAYERS) {
      const needsAnalysis =
        !currentImageHasLayerAnalysis && !isAnalyzing && !isLoadingSnapshot;
      if (needsAnalysis) {
        isEnteringLayersModeRef.current = true;
        void onAnalyzeLayers({
          onUsageConfirmed: () => {
            isEnteringLayersModeRef.current = false;
            setWorkMode(IMAGE_EDIT_WORK_MODE.LAYERS);
          },
        }).finally(() => {
          isEnteringLayersModeRef.current = false;
        });
        return;
      }
    }
    setWorkMode(nextMode);
    if (nextMode !== IMAGE_EDIT_WORK_MODE.LAYERS) {
      setEditingTextID(null);
      setImageLayerEditingTextID(null);
    }
  };

  const onRequestClose = async (reason) => {
    if (
      reason === "escapeKeyDown" &&
      (editingTextID || imageLayerEditingTextID)
    ) {
      return false;
    }
    if (isCloseConfirmOpenRef.current) return false;
    isCloseConfirmOpenRef.current = true;
    try {
      const result = await ConfirmAlert({
        title: t(MODAL_IMAGE_EDIT_CLOSE_TITLE),
        text: t(MODAL_IMAGE_EDIT_CLOSE_DESCRIPTION),
        confirmButtonText: t(BUTTON_CLOSE),
        cancelButtonText: t(TEMPLATES_KEEP_EDITING),
        icon: "warning",
      });
      if (!result?.isConfirmed) return false;
      onClose();
    } finally {
      isCloseConfirmOpenRef.current = false;
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    setIsSidebarOpen(true);
    dispatch(
      startListenerMergedFonts({
        onUpdatedDataCallback: (fonts) => {
          fontFamiliesRef.current = fonts || [];
          loadFontFaces({ fonts });
        },
        onReferenceAvailable: (refs) => {
          fontListenerRefs.current = refs;
        },
      })
    );
    return () => {
      dispatch(
        stopListenerMergedFonts({ listenerRefs: fontListenerRefs.current })
      );
    };
  }, [dispatch, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setWorkMode(IMAGE_EDIT_WORK_MODE.GENERATIVE);
    setImageUrl(originalImageUrl);
    setBlockBaseImageUrl(originalImageUrl);
    setCheckpointPromptsHistory([]);
    setImageLayerEditingTextID(null);
    acknowledgedLayerLossForUrlRef.current = null;
  }, [isOpen, itemID, originalImageUrl]);

  useEffect(() => {
    acknowledgedLayerLossForUrlRef.current = null;
  }, [resolvedImageUrl]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!isOpen || needsReanalysis) return;
      if (isNativeEditableTarget(event.target)) return;
      if (isLayersMode && isCopyHotkey(event)) {
        if (copyLayer()) {
          event.preventDefault();
        }
        return;
      }
      if (isLayersMode && isPasteHotkey(event)) {
        if (pasteLayer()) {
          event.preventDefault();
        }
        return;
      }
      if (isLayersMode && isDeleteHotkey(event)) {
        if (deleteLayer()) {
          event.preventDefault();
        }
        return;
      }
      const isUndo =
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        event.key?.toLowerCase() === "z";
      const isRedo =
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key?.toLowerCase() === "z";
      if (isUndo) {
        event.preventDefault();
        undo();
        return;
      }
      if (isRedo) {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    copyLayer,
    deleteLayer,
    isLayersMode,
    isOpen,
    needsReanalysis,
    pasteLayer,
    redo,
    undo,
  ]);

  useEffect(() => {
    const loadBrand = async () => {
      const response = await dispatch(startGetBrandInformation());
      if (response.ok) {
        setBusinessLookAndFeel(response.data?.businessLookAndFeel || "");
        brandFontFamilyRef.current =
          response.data?.businessTypography?.fontFamily || "";
      }
    };
    loadBrand();
  }, [dispatch]);

  const onChangeInstanceField = ({ key, value }) => {
    const node = selectedItem?.node;
    if (!node) return false;
    const highlightStyle = styleForInstanceHighlightField({
      node,
      nodeID: selectedID,
      key,
      value,
    });
    if (
      applyIfHighlightField(highlightStyle, (style) => {
        patchSelected({ patch: { style } });
      })
    ) {
      return;
    }
    if (key === "highlights") {
      const nextHighlights = Array.isArray(value) ? value : [];
      const style = { ...(node.style || {}) };
      if (nextHighlights.length) style.highlights = nextHighlights;
      else delete style.highlights;
      patchSelected({ patch: { style } });
      return;
    }
    if (key === "inlineReplacements") {
      const nextReplacements = Array.isArray(value) ? value : [];
      const style = { ...(node.style || {}) };
      if (nextReplacements.length) style.inlineReplacements = nextReplacements;
      else delete style.inlineReplacements;
      patchSelected({ patch: { style } });
      return;
    }
    switch (key) {
      case "name":
        patchSelected({ patch: { name: value } });
        return true;
      case "fixedValue":
        setTextValue(selectedID, value);
        return true;
      case "fontSelection":
        patchSelected({
          patch: {
            style: {
              ...(node.style || {}),
              ...(value && typeof value === "object" ? value : {}),
            },
          },
        });
        return;
      case "fontAssetRef":
      case "fontFaceRef":
      case "fontWeight":
      case "fontSize":
      case "lineHeight":
      case "letterSpacing":
      case "strokeWidth":
      case "strokeColor":
      case "shadowColor":
      case "shadowAngle":
      case "shadowDistance":
      case "shadowBlur":
      case "backgroundPadding":
      case "backgroundRadius":
      case "backgroundColor":
      case "color":
      case "align":
      case "orientation":
      case "textStyle":
        patchSelected({
          patch: {
            style: {
              ...(node.style || {}),
              [key]: value,
            },
          },
        });
        return;
      case "fill":
      case "radius":
        patchSelected({
          patch: {
            shape: {
              ...(node.shape || { kind: "roundedRectangle" }),
              [key]: value,
            },
          },
        });
        return;
      default: {
        const exhaustive = key;
        throw new Error(`Unhandled property: ${exhaustive}`);
      }
    }
  };

  const onChangeInstanceBox = ({
    x,
    y,
    width: nextWidth,
    height: nextHeight,
  }) => {
    if (!selectedItem || !width || !height) return;
    const box = selectedItem.box;
    updateBox({
      id: selectedID,
      nx: (x ?? box.x) / width,
      ny: (y ?? box.y) / height,
      nw: (nextWidth ?? box.width) / width,
      nh: (nextHeight ?? box.height) / height,
    });
    endCoalesce();
  };

  const onAddLayer = async ({ type, url, urls } = {}) => {
    const names = {
      text: t(TEMPLATES_PRODUCE_ADD_TEXT),
      image: t(TEMPLATES_PRODUCE_ADD_IMAGE),
    };
    switch (type) {
      case "image": {
        const imageUrls = (Array.isArray(urls) ? urls : [url]).filter(Boolean);
        if (!imageUrls.length) return;
        return addImageLayers({
          name: names.image,
          images: imageUrls.map((nextUrl) => ({
            id: dispatch(getKeyDatabase()),
            url: nextUrl,
          })),
        });
      }
      case "text":
        addLayer({
          type,
          url,
          name: names.text || t(TEMPLATES_NEW_LAYER),
          id: dispatch(getKeyDatabase()),
        });
        return;
      default: {
        const exhaustive = type;
        throw new Error(`Unhandled add layer type: ${exhaustive}`);
      }
    }
  };

  const onUploadInstanceAsset = async ({ file }) => {
    if (!file || typeof uploadFile !== "function" || !selectedID) return;
    setIsUploadingAsset(true);
    try {
      const uploadedUrl = await uploadFile({
        file,
        pathStorage: `socialMedia/${userID}/`,
        doCompression: false,
      });
      if (uploadedUrl) {
        replaceImage({
          nodeID: selectedID,
          url: uploadedUrl,
          name: selectedItem?.node?.name || selectedID,
        });
      }
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const onUploadInlineReplacementAsset = async ({ file, slotId }) => {
    if (!file || !slotId || typeof uploadFile !== "function" || !selectedID) {
      return;
    }
    setIsUploadingAsset(true);
    try {
      const uploadedUrl = await uploadFile({
        file,
        pathStorage: `socialMedia/${userID}/`,
        doCompression: false,
      });
      if (uploadedUrl) {
        assignInlineReplacementAsset({
          nodeID: selectedID,
          slotID: slotId,
          url: uploadedUrl,
          name: selectedItem?.node?.name || selectedID,
        });
      }
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const onChangeImageLayerField = ({ key, value }) => {
    if (!selectedLayerId) return false;
    const highlightStyle = styleForImageHighlightField({
      node: selectedImageLayer,
      nodeID: selectedLayerId,
      key,
      value,
    });
    if (
      applyIfHighlightField(highlightStyle, (style) => {
        updateLayerStyle(selectedLayerId, {
          highlights: style.highlights ?? null,
        });
      })
    ) {
      return;
    }
    if (key === "highlights") {
      const nextHighlights = Array.isArray(value) ? value : [];
      updateLayerStyle(selectedLayerId, {
        highlights: nextHighlights.length ? nextHighlights : null,
      });
      return;
    }
    if (key === "inlineReplacements") {
      const nextReplacements = Array.isArray(value) ? value : [];
      updateLayerStyle(selectedLayerId, {
        inlineReplacements: nextReplacements.length ? nextReplacements : null,
      });
      return;
    }
    switch (key) {
      case "name":
        renameImageLayer(selectedLayerId, value);
        return true;
      case "fixedValue":
        updateLayerText(selectedLayerId, value);
        return true;
      case "fontSelection":
        updateLayerStyle(
          selectedLayerId,
          value && typeof value === "object" ? value : {}
        );
        return;
      case "fontAssetRef":
      case "fontFaceRef":
      case "fontWeight":
      case "fontSize":
      case "lineHeight":
      case "letterSpacing":
      case "strokeWidth":
      case "strokeColor":
      case "shadowColor":
      case "shadowAngle":
      case "shadowDistance":
      case "shadowBlur":
      case "backgroundPadding":
      case "backgroundRadius":
      case "backgroundColor":
      case "color":
      case "align":
      case "orientation":
      case "textStyle":
        updateLayerStyle(selectedLayerId, { [key]: value });
        return;
      case "fill":
      case "radius":
        return;
      default: {
        const exhaustive = key;
        throw new Error(`Unhandled property: ${exhaustive}`);
      }
    }
  };

  const onChangeImageLayerBox = ({
    x,
    y,
    width: nextWidth,
    height: nextHeight,
  }) => {
    if (!selectedImageLayer || !canvasWidth || !canvasHeight) return;
    const current = selectedImageInspectorItem?.box;
    if (!current) return;
    updateLayerBox(
      selectedLayerId,
      pixelsToNormalized({
        x: x ?? current.x,
        y: y ?? current.y,
        width: nextWidth ?? current.width,
        height: nextHeight ?? current.height,
        displayWidth: canvasWidth,
        displayHeight: canvasHeight,
      })
    );
  };

  const onUploadImageLayerAsset = async ({ file }) => {
    if (!file || typeof uploadFile !== "function" || !selectedLayerId) return;
    setIsUploadingAsset(true);
    try {
      const uploadedUrl = await uploadFile({
        file,
        pathStorage: `socialMedia/${userID}/`,
        doCompression: false,
      });
      if (uploadedUrl) {
        replaceLayerImage(selectedLayerId, uploadedUrl);
      }
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const selectedImageInlineReplacementUrls = useMemo(
    () =>
      urlsFromInlineReplacementAssetRefs(
        selectedImageLayer?.style?.inlineReplacements
      ),
    [selectedImageLayer]
  );

  const onUploadImageLayerInlineReplacementAsset = async ({ file, slotId }) => {
    if (
      !file ||
      !slotId ||
      typeof uploadFile !== "function" ||
      !selectedLayerId
    ) {
      return;
    }
    const layerId = selectedLayerId;
    const slots = listStoredInlineReplacements(
      selectedImageLayer?.style?.inlineReplacements
    );
    if (!slots.some((slot) => slot.id === slotId)) return;
    setIsUploadingAsset(true);
    try {
      const uploadedUrl = await uploadFile({
        file,
        pathStorage: `socialMedia/${userID}/`,
        doCompression: false,
      });
      if (!uploadedUrl) return;
      updateLayerStyle(layerId, {
        inlineReplacements: slots.map((slot) =>
          slot.id === slotId ? { ...slot, assetRef: uploadedUrl } : slot
        ),
      });
    } finally {
      setIsUploadingAsset(false);
    }
  };

  const saveProduceUrl = async ({ url, nextWidth, nextHeight }) => {
    if (!produceID || !item?.id || !url) return { ok: false };
    const result = await dispatch(
      startSaveEditedProduceCreative({
        produceID,
        creativeID: item.id,
        url,
        width: nextWidth || item.width,
        height: nextHeight || item.height,
      })
    );
    if (!result?.ok) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_SAVE_ERROR),
        icon: "error",
      });
      return { ok: false };
    }
    return { ok: true };
  };

  const onSaveInstance = async () => {
    if (!produceID || !item?.id || isSaving) return;
    setIsSaving(true);
    try {
      const result = await dispatch(
        startSaveEditedProduceCreative({
          produceID,
          creativeID: item.id,
          instance: getPersistInstance(),
        })
      );
      if (!result?.ok) {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_SAVE_ERROR),
          icon: "error",
        });
        throw new Error(result?.message || "Failed to save edited creative");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onSaveGenerative = async (payload) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await saveProduceUrl({
        url: payload?.url || resolvedImageUrl,
        nextWidth: item?.width,
        nextHeight: item?.height,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const onSaveImageLayers = async (payload) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const result = await dispatch(
        startSaveEditedLayeredImage({
          layers,
          canvasWidth,
          canvasHeight,
        })
      );
      const uploadedUrl = result?.data?.url;
      if (!result?.ok || !uploadedUrl) {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_SAVE_ERROR),
          icon: "error",
        });
        throw new Error(result?.message || "Failed to save layered image");
      }
      await persistSnapshotNow(uploadedUrl);
      await saveProduceUrl({
        url: uploadedUrl,
        nextWidth: canvasWidth || item?.width,
        nextHeight: canvasHeight || item?.height,
      });
      if (payload) {
        setImageUrl(uploadedUrl);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onInstanceTextCommit = (nodeID, value) => {
    setTextValue(nodeID, value);
    // Clicking the properties panel blurs the editor: keep the edit open so
    // the phrase stays selected while its style is being changed.
    const keepEditing = keepInstanceTextEditingRef.current;
    keepInstanceTextEditingRef.current = false;
    if (!keepEditing) setEditingTextID(null);
    endCoalesce();
  };

  const onImageLayerTextCommit = (layerId, value) => {
    updateLayerText(layerId, value);
    const keepEditing = keepImageTextEditingRef.current;
    keepImageTextEditingRef.current = false;
    if (!keepEditing) setImageLayerEditingTextID(null);
  };

  const onAddTextLayer = () => {
    addTextLayer({ name: t(TEMPLATES_PRODUCE_ADD_TEXT) });
    setImageLayerEditingTextID(null);
  };

  const onDeleteImageLayer = (layerId) => {
    if (
      imageLayerEditingTextID &&
      (!layerId || imageLayerEditingTextID === layerId)
    ) {
      setImageLayerEditingTextID(null);
    }
    deleteImageLayer(layerId);
  };

  const onSendMessage = async (
    prompt,
    attachmentUrls = [],
    attachmentFileNames = {}
  ) => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || isProcessing || !blockBaseImageUrl) return;
    try {
      validateModalImageEditTextFields({
        prompt: trimmedPrompt,
        isPlanActive,
        isTrial,
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onSendMessage",
          capture: "exception",
        })
      );
      addMessage({
        id: Date.now().toString(),
        role: "assistant",
        content: `Error: ${error?.message || "The request could not be processed."}`,
        timestamp: new Date(),
        isError: true,
      });
      return;
    }

    const canContinue = await confirmAiEditLosesLayers();
    if (!canContinue) return;

    const isBlocked = await dispatch(
      getIsBlockedActions({
        action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
        showUsageConfirmationAlert: true,
        multiplier: 1,
      })
    );
    if (isBlocked) return;

    const resolvedAttachmentUrls = Array.isArray(attachmentUrls)
      ? attachmentUrls
      : [];
    const promptsOrdered = buildEditImagePromptsForJob({
      history: checkpointPromptsHistory,
      prompt: trimmedPrompt,
      attachmentUrls: resolvedAttachmentUrls,
    });
    addMessage({
      id: Date.now().toString(),
      role: "user",
      content: trimmedPrompt,
      timestamp: new Date(),
      imageUrl: resolvedImageUrl,
      attachmentUrls: resolvedAttachmentUrls,
      attachmentFileNames: attachmentFileNames || {},
    });
    addMessage(createThinkingMessage());

    await editImage(
      blockBaseImageUrl,
      promptsOrdered,
      businessLookAndFeel,
      (newImageUrl, aiResponse) => {
        setImageUrl(newImageUrl);
        replaceThinkingMessage(aiResponse);
        const completedTurns = promptsOrdered.map((turn, index) =>
          index === promptsOrdered.length - 1
            ? { ...turn, resultUrl: newImageUrl }
            : turn
        );
        const completedBlockSize = completedTurns.length;
        if (completedBlockSize >= PROMPTS_CHECKPOINT_SIZE) {
          setBlockBaseImageUrl(newImageUrl);
          setCheckpointPromptsHistory([]);
        } else {
          setCheckpointPromptsHistory(completedTurns);
        }
      },
      (errorMessageText) => {
        replaceThinkingMessage(errorMessageText);
      }
    );
  };

  const onImageSelect = (selectedImageUrl) => {
    setImageUrl(selectedImageUrl);
  };

  const renderSidebarBody = () => {
    if (!isLayersMode) {
      return (
        <SingleChatMessages
          messages={chatMessages}
          currentImageUrl={resolvedImageUrl}
          onImageSelect={onImageSelect}
        />
      );
    }
    if (useInstanceLayers) {
      return (
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <InstanceLayersSidebar
            nodes={nodes}
            nodeLayouts={nodeLayouts}
            items={items}
            selectedID={selectedID}
            selectedIDs={selectedIDs}
            expandedIDs={expandedIDs}
            onSelectLayer={(id, event) => {
              blurActiveEditable(event);
              selectLayer(id, event);
            }}
            onRenameLayer={renameLayer}
            onToggleExpand={(id) =>
              setExpandedIDs((current) => ({
                ...current,
                [id]: !current[id],
              }))
            }
            onReorderSiblings={reorderSiblings}
            onAddLayer={onAddLayer}
            onCopyLayer={(id) => {
              if (!copyLayer(id)) return;
              pasteLayer();
            }}
            onDeleteLayer={deleteLayer}
            onToggleLock={toggleLock}
            onToggleVisible={toggleVisible}
          />
        </Box>
      );
    }
    return (
      <LayersSidebar
        layers={layers}
        selectedLayerId={selectedLayerId}
        selectedLayerIds={selectedLayerIds}
        onSelectLayer={(id, event) => {
          blurActiveEditable(event);
          selectImageLayer(id, event);
        }}
        isAnalyzing={isAnalyzing}
        isLoadingSnapshot={isLoadingSnapshot}
        errorMessage={hasSnapshot ? null : errorMessage}
        onAnalyze={hasSnapshot ? null : onAnalyzeLayers}
        onAddLayerUrls={addLayersFromUrls}
        onAddTextLayer={onAddTextLayer}
        onReorderLayers={reorderLayers}
        onRenameLayer={renameImageLayer}
        onDuplicateLayer={duplicateLayer}
        onDeleteLayer={onDeleteImageLayer}
        onToggleLock={toggleLayerLock}
        onToggleVisible={toggleLayerVisible}
      />
    );
  };

  return (
    <ModalBasicLayout
      modalOpen={isOpen}
      onCloseModal={onRequestClose}
      fullScreen={true}
      maxWidth={false}
      fullWidth={true}
      hasTitle={false}
      ariaLabel={t(TEMPLATES_PRODUCE_EDIT_TITLE)}
      sxContent={{
        py: 0,
        px: 0,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        position: "relative",
      }}
      sxPaper={{
        height: "100vh",
        maxHeight: "100vh",
      }}
    >
      <ImageEditTopBar
        title={t(TEMPLATES_PRODUCE_EDIT_TITLE)}
        imageUrl={resolvedImageUrl}
        initialImageUrl={originalImageUrl}
        onCallbackSave={
          useInstanceLayers
            ? onSaveInstance
            : isLayersMode
              ? onSaveImageLayers
              : onSaveGenerative
        }
        onClose={onRequestClose}
        onCloseAfterSave={onClose}
        showSaveAlert={false}
        saveDisabled={
          useInstanceLayers
            ? isSaving
            : isLayersMode
              ? !layersDirty || isSaving
              : isSaving || resolvedImageUrl === originalImageUrl
        }
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        <ResponsiveSidebarContainer
          isSmallScreen={isSmallScreen}
          open={isSidebarOpen}
          onClose={toggleSidebar}
          paperWidth={isLayersMode ? layersSidebarWidth : 420}
        >
          {isSidebarOpen ? (
            <ChatSidebar
              isSmallScreen={isSmallScreen}
              title={t(TEMPLATES_PRODUCE_EDIT_TITLE)}
              isProcessing={isProcessing}
              onSendMessage={onSendMessage}
              onToggleSidebar={toggleSidebar}
              scrollRef={chatScrollRef}
              hideHeader
              hideInput={isLayersMode}
              width={isLayersMode ? layersSidebarWidth : 420}
              resizable={isLayersMode}
              onResizePointerDown={onResizeLayersSidebar}
              headerExtra={
                <WorkModeSelector
                  selectedValue={workMode}
                  disabled={isAnalyzing}
                  onChange={onWorkModeChange}
                />
              }
            >
              {renderSidebarBody()}
            </ChatSidebar>
          ) : null}
        </ResponsiveSidebarContainer>
        {isLayersMode && useInstanceLayers ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
            }}
          >
            <InstanceCanvas
              items={items}
              canvasWidth={width}
              canvasHeight={height}
              selectedLayerId={selectedID}
              selectedLayerIds={selectedIDs}
              editingTextID={editingTextID}
              onTextSelectionChange={onInstanceTextSelectionChange}
              selectedTextNodeID={instanceTextSelection?.nodeID || null}
              selectedTextPhrase={instanceTextSelection?.phrase || ""}
              isSidebarOpen={isSidebarOpen}
              previewUrl={resolvedImageUrl}
              onToggleSidebar={() => setIsSidebarOpen(true)}
              onSelectLayer={(id, event, options) => {
                blurActiveEditable(event);
                selectLayer(id, event, options);
              }}
              onSelectLayers={(ids, event) => {
                blurActiveEditable(event);
                selectLayers(ids, event);
              }}
              onLayerBoxChange={updateBox}
              onLayerBoxesChange={updateBoxes}
              onStartTextEdit={setEditingTextID}
              onTextCommit={onInstanceTextCommit}
              showCloseButton={false}
              showSaveActions={false}
            />
            <Box sx={propertiesPanelSx}>
              <InstancePropertiesPanel
                selectedItem={selectedItem}
                isUploadingAsset={isUploadingAsset}
                isTextRangeSelected={isInstanceTextRangeSelected}
                selectedPhrase={instanceSelectedPhrase}
                alignOnly={selectedIDs.length >= 2}
                onAlign={onAlignInstanceLayers}
                onChangeField={onChangeInstanceField}
                onChangeBox={onChangeInstanceBox}
                onUploadAsset={onUploadInstanceAsset}
                onUploadInlineReplacementAsset={onUploadInlineReplacementAsset}
                inlineReplacementUrls={selectedItem?.inlineReplacementUrls}
              />
            </Box>
          </Box>
        ) : null}
        {isLayersMode && !useInstanceLayers ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              height: "100%",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
            }}
          >
            <LayerCanvas
              imageUrl={resolvedImageUrl}
              initialImageUrl={originalImageUrl}
              layers={layers}
              canvasWidth={canvasWidth}
              canvasHeight={canvasHeight}
              isAnalyzing={!hasSnapshot && isAnalyzing}
              selectedLayerId={selectedLayerId}
              selectedLayerIds={selectedLayerIds}
              editingTextID={imageLayerEditingTextID}
              onTextSelectionChange={onImageTextSelectionChange}
              selectedTextNodeID={imageTextSelection?.nodeID || null}
              selectedTextPhrase={imageTextSelection?.phrase || ""}
              onSelectLayer={(id, event, options) => {
                blurActiveEditable(event);
                selectImageLayer(id, event, options);
              }}
              onSelectLayers={(ids, event) => {
                blurActiveEditable(event);
                selectImageLayers(ids, event);
              }}
              onLayerBoxChange={updateLayerBox}
              onLayerBoxesChange={updateLayerBoxes}
              onStartTextEdit={setImageLayerEditingTextID}
              onTextCommit={onImageLayerTextCommit}
              isSidebarOpen={isSidebarOpen}
              onToggleSidebar={toggleSidebar}
              showCloseButton={false}
              showSaveActions={false}
            />
            <Box sx={propertiesPanelSx}>
              <InstancePropertiesPanel
                selectedItem={selectedImageInspectorItem}
                isUploadingAsset={isUploadingAsset}
                isTextRangeSelected={isImageTextRangeSelected}
                selectedPhrase={imageSelectedPhrase}
                alignOnly={selectedLayerIds.length >= 2}
                onAlign={onAlignImageLayers}
                onChangeField={onChangeImageLayerField}
                onChangeBox={onChangeImageLayerBox}
                onUploadAsset={onUploadImageLayerAsset}
                onUploadInlineReplacementAsset={
                  onUploadImageLayerInlineReplacementAsset
                }
                inlineReplacementUrls={selectedImageInlineReplacementUrls}
              />
            </Box>
          </Box>
        ) : null}
        {!isLayersMode ? (
          <ImageCanvas
            imageUrl={resolvedImageUrl}
            initialImageUrl={originalImageUrl}
            isEditing={isEditing}
            isSidebarOpen={isSidebarOpen}
            onToggleSidebar={toggleSidebar}
            showCloseButton={false}
            showSaveActions={false}
          />
        ) : null}
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalCreativeTemplateEdit;
